import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState
} from 'react'

import { fetchAll } from '@/services/collect/fetchAll'
import { BaseFilters, useCommonContext } from '@/hooks/use-common-context'

interface ICollectProps {
  activeDrawer: boolean
  collect: any
  collectRefetchTrigger: number
  currentFilters: BaseFilters | undefined
  handleFilter: (filtros: BaseFilters) => Promise<void>
  handleLimitChange: (newLimit: number) => void
  handleSelectItem: (item: string[], action: boolean) => void
  isLoading: boolean
  limit: number
  page: number
  results: number
  search: string
  selectItems: any[]
  setActiveDrawer: React.Dispatch<React.SetStateAction<boolean>>
  setCollect: React.Dispatch<React.SetStateAction<any>>
  setCurrentFilters: React.Dispatch<React.SetStateAction<BaseFilters | undefined>>
  setLimit: React.Dispatch<React.SetStateAction<number>>
  setPage: React.Dispatch<React.SetStateAction<number>>
  setResults: React.Dispatch<React.SetStateAction<number>>
  setSearch: React.Dispatch<React.SetStateAction<string>>
  setSelectItems: React.Dispatch<React.SetStateAction<any[]>>
  setTableData: React.Dispatch<React.SetStateAction<any[]>>
  tableData: any[]
  triggerCollectRefetch: () => void
}

const CollectContext = createContext<ICollectProps | undefined>(undefined)

const CollectProvider = ({ children }: { children: ReactNode }) => {
  const [collect, setCollect] = useState<any>(null)
  const [collectRefetchTrigger, setCollectRefetchTrigger] = useState(0)

  const triggerCollectRefetch = () => setCollectRefetchTrigger(prev => prev + 1)

  const {
    activeDrawer, currentFilters, handleFilter, handleLimitChange,
    handleSelectItem, isLoading, limit, page, results, search, selectItems,
    setActiveDrawer, setCurrentFilters, setLimit, setPage, setResults,
    setSearch, setSelectItems, setTableData, tableData,
  } = useCommonContext(fetchAll, { hasAgrupamento: true, adjustEndDate: true, afterFilter: triggerCollectRefetch })

  const contextValue = useMemo(
    () => ({
      activeDrawer,
      collect,
      collectRefetchTrigger,
      currentFilters,
      handleFilter,
      handleLimitChange,
      handleSelectItem,
      isLoading,
      limit,
      page,
      results,
      search,
      selectItems,
      setActiveDrawer,
      setCollect,
      setCurrentFilters,
      setLimit,
      setPage,
      setResults,
      setSearch,
      setSelectItems,
      setTableData,
      tableData,
      triggerCollectRefetch,
    }),
    [
      activeDrawer,
      collect,
      collectRefetchTrigger,
      currentFilters,
      handleFilter,
      handleLimitChange,
      handleSelectItem,
      isLoading,
      limit,
      page,
      results,
      search,
      selectItems,
      tableData,
    ]
  )

  return (
    <CollectContext.Provider value={contextValue}>
      {children}
    </CollectContext.Provider>
  )
}

const useCollectContext = () => {
  const context = useContext(CollectContext)
  if (context === undefined) {
    throw new Error('useCollectContext must be used within a CollectProvider')
  }
  return context
}

export { CollectProvider, useCollectContext }
